import type { CSSProperties, ReactNode } from 'react';
import {
  hasEmailSecurityContent,
  hasPostureContent,
  PdfExecDigestSection,
  PdfEmailSecuritySection,
  PdfPostureSection,
  type ReportTelemetry,
} from './charts/PdfChartSections';
import { format } from 'date-fns';
import { InlineMd, Md } from '../markdown';
import type {
  AuthoredIndustryCard,
  AuthoredRetroCard,
  AuthoredSections,
  ReportDetail,
} from '../../../data/types';

import './pdf-report.css';

export const PDF_TEMPLATE_ID = 'alexein-monthly';
export const PDF_TEMPLATE_VERSION = 1;

export type PdfSectionKey =
  | 'cover'
  | 'executive'
  | 'metrics'
  | 'posture'
  | 'emailSecurity'
  | 'execDigest'
  | 'incidents'
  | 'industry'
  | 'serviceDelivery'
  | 'closing';

export interface PdfTheme {
  navy: string;
  blue: string;
  lime: string;
  paper: string;
  ink: string;
  muted: string;
}

export interface PdfTemplateConfig {
  id: string;
  version: number;
  coverVariant: 'editorial' | 'minimal' | 'customer-branded';
  sectionOrder: PdfSectionKey[];
  enabledSections: Record<PdfSectionKey, boolean>;
  startOnNewPage: Record<PdfSectionKey, boolean>;
  theme: PdfTheme;
}

export const DEFAULT_PDF_TEMPLATE: PdfTemplateConfig = {
  id: PDF_TEMPLATE_ID,
  version: PDF_TEMPLATE_VERSION,
  coverVariant: 'editorial',
  sectionOrder: [
    'cover',
    'executive',
    'metrics',
    'posture',
    'emailSecurity',
    'execDigest',
    'incidents',
    'industry',
    'serviceDelivery',
    'closing',
  ],
  enabledSections: {
    cover: true,
    executive: true,
    metrics: true,
    // Chart sections default off: the monthly report's layout must not change
    // just because the executive template gained them.
    posture: false,
    emailSecurity: false,
    execDigest: false,
    incidents: true,
    industry: true,
    serviceDelivery: true,
    closing: true,
  },
  startOnNewPage: {
    cover: true,
    executive: true,
    metrics: true,
    posture: true,
    emailSecurity: true,
    execDigest: true,
    incidents: true,
    industry: true,
    serviceDelivery: true,
    closing: true,
  },
  theme: {
    navy: '#09193b',
    blue: '#0000ff',
    lime: '#b9ff4d',
    paper: '#f4f3ef',
    ink: '#09193b',
    muted: '#595959',
  },
};

export const PDF_TEMPLATE_REGISTRY: Readonly<Record<string, PdfTemplateConfig>> = {
  [`${DEFAULT_PDF_TEMPLATE.id}@${DEFAULT_PDF_TEMPLATE.version}`]: DEFAULT_PDF_TEMPLATE,
};

export function resolvePdfTemplate(id = PDF_TEMPLATE_ID, version = PDF_TEMPLATE_VERSION): PdfTemplateConfig {
  const template = PDF_TEMPLATE_REGISTRY[`${id}@${version}`];
  if (!template) throw new Error(`Unknown PDF template ${id}@${version}`);
  return template;
}

export function chunkForPdf<T>(items: T[], size: number): T[][] {
  if (size < 1) throw new Error('PDF chunk size must be at least 1');
  const pages: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    pages.push(items.slice(index, index + size));
  }
  return pages;
}

export function PdfPage({
  children,
  variant = 'standard',
  section,
  issueLabel,
  pageLabel,
  className,
}: {
  children: ReactNode;
  variant?: 'standard' | 'cover' | 'paper';
  section: PdfSectionKey;
  issueLabel?: string;
  pageLabel?: string;
  className?: string;
}) {
  const pageIdentity = pageLabel ?? section;
  const identityTint = Array.from(pageIdentity).reduce((sum, character) => sum + character.charCodeAt(0), 0) % 5;
  const pageBackground = variant === 'paper'
    ? `rgb(244 243 ${238 + identityTint})`
    : `rgb(255 255 ${251 + identityTint})`;

  return (
    <section
      className={`pdf-page pdf-page--${variant}${className ? ` ${className}` : ''}`}
      data-pdf-page
      data-pdf-section={section}
      style={variant === 'cover' ? undefined : { backgroundColor: pageBackground }}
    >
      {variant !== 'cover' && <PdfPageHeader issueLabel={issueLabel ?? ''} pageLabel={pageLabel ?? section} />}
      <div className="pdf-page__content">{children}</div>
      {variant !== 'cover' && <PdfPageFooter pageLabel={pageLabel} />}
    </section>
  );
}

export function PdfPageHeader({ issueLabel, pageLabel }: { issueLabel: string; pageLabel: string }) {
  return (
    <div className="pdf-page-header" role="banner">
      <span>{pageLabel}</span>
      <span>{`${issueLabel} - ${pageLabel}`}</span>
    </div>
  );
}

export function PdfPageFooter({ pageLabel }: { pageLabel?: string }) {
  const shortLabel = (pageLabel ?? 'REPORT')
    .replace('INCIDENT RETROSPECTIVE', 'INCIDENTS')
    .replace('INDUSTRY INTELLIGENCE', 'INDUSTRY')
    .replace('EXECUTIVE SUMMARY', 'SUMMARY')
    .replace('THE MONTH IN DATA', 'DATA')
    .replace('SERVICE DELIVERY', 'SERVICE')
    .replace('ABOUT THIS REPORT', 'ABOUT');

  return (
    <div className="pdf-page-footer" role="contentinfo">
      <span>{shortLabel}</span>
    </div>
  );
}

export function KeepTogether({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`pdf-keep ${className}`}>{children}</div>;
}

export function PageBreak() {
  return <div className="pdf-page-break" aria-hidden="true" />;
}

/**
 * The cover's masthead named the product "MONTHLY SECURITY REPORT" regardless of
 * what was actually generated, so the executive summary shipped mislabelled.
 */
const REPORT_TYPE_LABELS: Record<string, string> = {
  monthly: 'MONTHLY SECURITY REPORT',
  weekly: 'WEEKLY SECURITY REPORT',
  executive: 'EXECUTIVE SUMMARY',
  compliance: 'COMPLIANCE REPORT',
  ad_hoc: 'SECURITY REPORT',
};


/**
 * The three blocks that turn the executive cover from a title page into a
 * briefing.
 *
 * The attention block is the point of this: without it the cover looks
 * identical whether the month was quiet or catastrophic — "escalated: 0" is one
 * small stat among four — and this is the page an executive may be the only one
 * they read. It is deliberately conditional: a confident single line when
 * nothing needed hands-on work, and a named, prominent block when it did.
 */
function CoverBriefing({
  report,
  sections,
}: {
  report: ReportDetail;
  sections: AuthoredSections;
}) {
  const data = report.structuredData;
  const severity = data?.volume?.bySeverity;
  const seriousCount = (severity?.critical ?? 0) + (severity?.high ?? 0);

  // Escalation, not severity, is what "requires your attention" means. Severity
  // is Defender's label on the detection; escalation is whether a human had to
  // act. Driving this block off severity put "9 incidents of high or critical
  // severity" next to a stat strip reading ESCALATED: 0 on the same page —
  // both true, and together they read as a document contradicting itself.
  const escalated =
    (data?.funnel ?? []).find((f) => f.k?.toLowerCase() === 'escalated')?.n ?? 0;

  // topIncidents legitimately repeats titles — three separate "Files Copied to
  // USB Drives" incidents here — so collapse them rather than printing the same
  // line twice and looking broken.
  const escalatedIncidents = (() => {
    const counts = new Map<string, number>();
    for (const i of data?.topIncidents ?? []) {
      const sev = i.severity?.toLowerCase();
      if (sev !== 'critical' && sev !== 'high') continue;
      const key = `${i.severity}|${i.title}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()].map(([key, count]) => {
      const [sev, ...rest] = key.split('|');
      return { severity: sev, title: rest.join('|'), count };
    });
  })();

  const sd = sections.serviceDelivery;
  const delivered: string[] = [];
  if (sd) {
    if (sd.hunts?.count) delivered.push(`${sd.hunts.count} proactive threat hunt${sd.hunts.count === 1 ? '' : 's'}`);
    if (sd.analyticsEnabled?.length) delivered.push(`${sd.analyticsEnabled.length} new detection rule${sd.analyticsEnabled.length === 1 ? '' : 's'} enabled`);
    if (sd.solutionsUpdated?.length) delivered.push(`${sd.solutionsUpdated.length} solution${sd.solutionsUpdated.length === 1 ? '' : 's'} updated`);
    if (sd.cloudAppDiscovery?.length) delivered.push(`${sd.cloudAppDiscovery.length} cloud app${sd.cloudAppDiscovery.length === 1 ? '' : 's'} reviewed`);
  }

  return (
    <>
      <div className={`pdf-cover-attention ${escalated > 0 ? 'pdf-cover-attention--raised' : ''}`}>
        {escalated > 0 ? (
          <>
            <strong>Requires your attention</strong>
            <p>
              {escalated} incident{escalated === 1 ? '' : 's'} needed hands-on SOC
              intervention this period.
            </p>
            <ul>
              {escalatedIncidents.slice(0, 3).map((i) => (
                <li key={`${i.severity}-${i.title}`}>
                  <em>{i.severity}</em> {i.title}
                  {i.count > 1 && ` (×${i.count})`}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <strong>No escalations this period</strong>
            <p>
              {seriousCount > 0
                ? `${seriousCount} high-severity detection${seriousCount === 1 ? '' : 's'} ${seriousCount === 1 ? 'was' : 'were'} raised and resolved at triage, with none requiring hands-on SOC intervention.`
                : 'Nothing required hands-on SOC intervention. Every detection was resolved at triage.'}
            </p>
          </>
        )}
      </div>

      {/*
        No "Recommended next step" block here.

        It rendered `secure_score_top_actions` as "<Microsoft control name> —
        worth N points on your Secure Score": both things the brief rejected in
        one sentence, a Secure Score point value ("+9 pts for Safe Mode" means
        nothing to an exec) and an IT-targeted action item. The titles are
        Microsoft's raw control names — "Identify Entra ID privileged accounts
        that are also privileged in Active Directory" — so no wording makes them
        an executive's next step.

        This cover is shared by both templates and the block appeared as soon as
        a tenant had telemetry, so it was invisible in Windermere's July monthly
        (generated before their first collection) and their August one would
        have been the first time the customer ever saw it. The metric stays
        collected — it is the natural upsell surface — it is simply not
        rendered, which is what the handoff always claimed was already true.
      */}
      {delivered.length > 0 && (
        <div className="pdf-cover-briefing">
          <div>
            <strong>Beyond monitoring</strong>
            <p>{delivered.join(' · ')}</p>
          </div>
        </div>
      )}
    </>
  );
}

function CoverPage({
  report,
  sections,
  coverVariant = 'editorial',
  showBriefing = false,
}: {
  report: ReportDetail;
  sections: AuthoredSections;
  coverVariant?: PdfTemplateConfig['coverVariant'];
  /** Executive products put the briefing blocks on the cover; the monthly has
   *  whole sections for the same material and must not repeat it. */
  showBriefing?: boolean;
}) {
  const month = format(new Date(report.periodStart), 'MMMM yyyy');
  const period = `${format(new Date(report.periodStart), 'd MMM')} - ${format(new Date(report.periodEnd), 'd MMM yyyy')}`;
  const masthead = REPORT_TYPE_LABELS[report.reportType ?? 'monthly'] ?? 'SECURITY REPORT';
  return (
    <PdfPage variant="cover" section="cover" className={`pdf-cover--${coverVariant}`}>
      <div className="pdf-cover__brand">
        <img src="/alexein-logo.svg" alt="Alexein" />
        <span>{masthead}</span>
      </div>
      {/*
        main + lower share a flow wrapper. On the editorial cover the wrapper is
        `display: contents` so both keep their original absolute positions; on the
        minimal cover it becomes the positioned column and the children flow.
        Fixed offsets could not survive a content-driven dek — Windermere's ran to
        three lines where AAT's ran to two, and the stat strip was positioned into
        it. The renderer's check compares scrollHeight to clientHeight, so it sees
        overflow but never overlap.
      */}
      <div className="pdf-cover__flow">
      <div className={`pdf-cover__main ${sections.cover.headline.length > 72 ? 'pdf-cover__main--compact' : ''}`}>
        <p className="pdf-kicker">{sections.cover.kicker || `Issue ${sections.issueNumber} - ${month}`}</p>
        <h1>{sections.cover.headline || report.reportTitle}</h1>
        <p className="pdf-cover__dek"><InlineMd>{sections.cover.dek}</InlineMd></p>
      </div>
      {/*
        The strip and the note share one absolutely-positioned block, with the
        note in normal flow beneath it. Positioning the note at its own fixed
        offset collided with the strip as soon as a stat label wrapped to two
        lines — and the renderer's overflow check compares scrollHeight to
        clientHeight, so it cannot see an overlap.
      */}
      <div className="pdf-cover__lower">
        <div className="pdf-cover__glance">
          {(sections.cover.atAGlance ?? []).slice(0, 4).map((item) => (
            <div className="pdf-cover-stat" key={item.k}>
              <span>{item.k}</span>
              <strong>{item.n}</strong>
              {item.d && <small>{item.d}</small>}
            </div>
          ))}
        </div>
        {showBriefing && (
          <CoverBriefing report={report} sections={sections} />
        )}
      </div>
      </div>
      <div className="pdf-cover__meta">
        <div><span>Prepared for</span><strong>{report.tenantDisplayName}</strong></div>
        <div><span>Reporting period</span><strong>{period}</strong></div>
        <div><span>Issue</span><strong>{String(sections.issueNumber).padStart(2, '0')}</strong></div>
      </div>
    </PdfPage>
  );
}

function ExecutivePage({ sections, issueLabel }: { sections: AuthoredSections; issueLabel: string }) {
  const spread = sections.spread;
  return (
    <PdfPage section="executive" issueLabel={issueLabel} pageLabel="EXECUTIVE SUMMARY">
      <div className="pdf-section-heading">
        <p>{spread.eyebrow}</p>
        <h2>{spread.bigHeadline}</h2>
        <div className="pdf-rule" />
      </div>
      <div className="pdf-executive-grid">
        <div>
          <p className="pdf-lede"><InlineMd>{spread.lede}</InlineMd></p>
          <div className="pdf-body-copy">
            {spread.body.slice(0, 4).map((paragraph, index) => (
              <p key={index}><InlineMd>{paragraph}</InlineMd></p>
            ))}
          </div>
          <blockquote>{spread.pullQuote.text}<cite>{spread.pullQuote.cite}</cite></blockquote>
        </div>
        <div className="pdf-stat-stack">
          {sections.statCards.slice(0, 4).map((card, index) => (
            <KeepTogether className={index === 0 ? 'pdf-stat pdf-stat--hero' : 'pdf-stat'} key={card.k}>
              <span>{card.k}</span>
              <strong>{card.n}{card.unit && <small>{card.unit}</small>}</strong>
              <p><InlineMd>{card.narrative}</InlineMd></p>
            </KeepTogether>
          ))}
        </div>
      </div>
    </PdfPage>
  );
}

/**
 * Page wrapper for the telemetry chart sections. Mirrors MetricsPage's shell —
 * the sections themselves stay presentation-only so they can also be dropped
 * into the on-screen preview without dragging page chrome along.
 */
function ChartPage({
  title,
  issueLabel,
  children,
  subtitle = 'Measured across the reporting period.',
}: {
  title: string;
  issueLabel: string;
  children: React.ReactNode;
  /** Pass null on pages that open with their own authored copy. */
  subtitle?: string | null;
}) {
  return (
    <PdfPage section="metrics" issueLabel={issueLabel} pageLabel={title.toUpperCase()}>
      <div className="pdf-section-heading pdf-section-heading--compact">
        <p>{title.toUpperCase()}</p>
        {subtitle && <h2>{subtitle}</h2>}
        <div className="pdf-rule" />
      </div>
      {children}
    </PdfPage>
  );
}

function MetricsPage({ report, sections, issueLabel }: { report: ReportDetail; sections: AuthoredSections; issueLabel: string }) {
  const volume = report.structuredData?.volume;
  const maxSeverity = Math.max(1, ...Object.values(volume?.bySeverity ?? {}).map(Number));
  const mitre = (report.structuredData?.mitre ?? []).slice(0, 8);
  return (
    <PdfPage section="metrics" issueLabel={issueLabel} pageLabel="THE MONTH IN DATA">
      <div className="pdf-section-heading pdf-section-heading--compact">
        <p>THE MONTH IN DATA</p>
        <h2>Signals, outcomes and observed techniques.</h2>
        <div className="pdf-rule" />
      </div>
      <p className="pdf-section-intro"><InlineMd>{sections.featureIntro}</InlineMd></p>
      <div className="pdf-metrics-grid">
        <KeepTogether className="pdf-panel">
          <div className="pdf-panel__title">Incident volume by severity</div>
          {Object.entries(volume?.bySeverity ?? {}).map(([severity, count]) => (
            <div className="pdf-bar-row" key={severity}>
              <span>{severity}</span>
              <div><i style={{ width: `${Math.max(3, (Number(count) / maxSeverity) * 100)}%` }} /></div>
              <strong>{count}</strong>
            </div>
          ))}
          <p className="pdf-caption"><InlineMd>{sections.chartCaptions.volumeMttr}</InlineMd></p>
        </KeepTogether>
        <KeepTogether className="pdf-panel pdf-panel--navy">
          <span className="pdf-metric-label">Total incidents</span>
          <strong className="pdf-metric-number">{volume?.total ?? 0}</strong>
          <p>{volume?.trendPct == null ? 'First complete reporting period.' : `${Math.abs(volume.trendPct)}% ${volume.trendPct > 0 ? 'increase' : 'decrease'} from the previous period.`}</p>
          <div className="pdf-mini-metrics">
            <div><span>Resolved</span><strong>{report.structuredData?.resolution?.resolvedCount ?? 0}</strong></div>
            <div><span>Average resolution</span><strong>{report.structuredData?.resolution?.avgHours ?? '-'}h</strong></div>
          </div>
        </KeepTogether>
      </div>
      <KeepTogether className="pdf-panel pdf-mitre-panel">
        <div className="pdf-panel__title">MITRE ATT&amp;CK observations</div>
        <div className="pdf-mitre-grid">
          {mitre.map((item) => (
            <div className="pdf-mitre-item" key={`${item.tactic}-${item.technique}`}>
              <span>{item.tactic}</span><strong>{item.technique}</strong><b>{item.count}</b>
            </div>
          ))}
          {mitre.length === 0 && <p>No MITRE techniques were recorded for this period.</p>}
        </div>
        <p className="pdf-caption"><InlineMd>{sections.chartCaptions.heatmap}</InlineMd></p>
      </KeepTogether>
    </PdfPage>
  );
}

function IncidentPage({ cards, issueLabel, pageIndex, pageTotal }: { cards: AuthoredRetroCard[]; issueLabel: string; pageIndex: number; pageTotal: number }) {
  return (
    <PdfPage section="incidents" issueLabel={issueLabel} pageLabel={`INCIDENT RETROSPECTIVE ${pageIndex}/${pageTotal}`}>
      <div className="pdf-section-heading pdf-section-heading--compact">
        <p>{`INCIDENT RETROSPECTIVE - ${pageIndex}/${pageTotal}`}</p>
        <h2>{pageIndex === 1 ? 'The cases that shaped the month.' : 'What we learned and changed.'}</h2>
        <div className="pdf-rule" />
      </div>
      <div className="pdf-incident-list">
        {cards.map((card, index) => (
          <KeepTogether className="pdf-incident" key={`${card.title}-${index}`}>
            <div className="pdf-incident__index">{String((pageIndex - 1) * 2 + index + 1).padStart(2, '0')}</div>
            <div className="pdf-incident__main">
              <h3>{card.title}</h3>
              <div className="pdf-tags">{card.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
              {card.narrative.slice(0, 3).map((p, i) => <p key={i}><InlineMd>{p}</InlineMd></p>)}
            </div>
            <div className="pdf-incident__lessons">
              <h4>Lessons and actions</h4>
              <ul>{card.lessons.slice(0, 5).map((lesson) => <li key={lesson}><InlineMd>{lesson}</InlineMd></li>)}</ul>
            </div>
          </KeepTogether>
        ))}
      </div>
    </PdfPage>
  );
}

function IndustryPage({ cards, issueLabel, pageIndex, pageTotal }: { cards: AuthoredIndustryCard[]; issueLabel: string; pageIndex: number; pageTotal: number }) {
  return (
    <PdfPage variant="paper" section="industry" issueLabel={issueLabel} pageLabel={`INDUSTRY INTELLIGENCE ${pageIndex}/${pageTotal}`}>
      <div className="pdf-section-heading pdf-section-heading--compact">
        <p>{`PAGE ${pageIndex} OF ${pageTotal}`}</p>
        <h2>{pageIndex === 1 ? 'Outside signals worth your attention.' : `Industry intelligence continued - ${pageIndex}/${pageTotal}.`}</h2>
        <div className="pdf-rule" />
      </div>
      <div className="pdf-industry-list">
        {cards.map((card) => (
          <KeepTogether className="pdf-industry-card" key={card.title}>
            {card.image ? <img src={card.image} alt="" /> : (
              <div
                className="pdf-image-placeholder"
                style={{ background: `rgb(232 237 ${240 + Array.from(card.cat).reduce((sum, character) => sum + character.charCodeAt(0), 0) % 10})` }}
              >
                <span>{card.cat}</span>
              </div>
            )}
            <div>
              <span className="pdf-category">{card.cat}</span>
              <h3>{card.title}</h3>
              <Md className="pdf-markdown">{card.body}</Md>
              {/* Suppressed when the analyst hasn't authored one — the
                  generator leaves `applied` empty in that case, and the label
                  on its own reads as a missing paragraph. */}
              {card.applied.trim() && (
                <p className="pdf-applied"><strong>Applied to your environment:</strong> <InlineMd>{card.applied}</InlineMd></p>
              )}
            </div>
          </KeepTogether>
        ))}
      </div>
    </PdfPage>
  );
}

/**
 * Service delivery pages.
 *
 * Everything here used to render onto a single PdfPage, unlike the incident and
 * industry sections which chunk. That made this section the one place where
 * analyst-entered content could grow past an A4 page — and because the renderer
 * is fail-closed on overflow, the result wasn't a clipped page, it was **no PDF
 * at all**, which in turn blocks approve and send behind 409 PDF_REQUIRED. A
 * customer with an ordinary amount of service-delivery detail could not have a
 * report published (prod axn-0005, 2026-08-11: overflow 58px).
 *
 * So the structured cards and the free-form "Other work" write-up now paginate:
 * the four groups plus hunting fill the first page, and otherWork starts a page
 * of its own, splitting across further pages on top-level markdown blocks when
 * it is long. Splitting on blank lines keeps list items and paragraphs intact —
 * we never break mid-construct, which would corrupt the markdown render.
 */
const SERVICE_OTHER_WORK_BLOCKS_PER_PAGE = 6;

function splitOtherWork(markdown: string): string[] {
  const blocks = markdown.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  if (blocks.length === 0) return [];
  return chunkForPdf(blocks, SERVICE_OTHER_WORK_BLOCKS_PER_PAGE).map((page) => page.join('\n\n'));
}

function ServiceDeliveryPages({ sections, issueLabel }: { sections: AuthoredSections; issueLabel: string }) {
  const service = sections.serviceDelivery;
  if (!service) return null;
  const groups = [
    ['Solutions updated', service.solutionsUpdated],
    ['Solutions installed', service.solutionsInstalled],
    ['Analytics updated', service.analyticsUpdated],
    ['Analytics enabled', service.analyticsEnabled],
  ] as const;
  const otherWorkPages = service.otherWork ? splitOtherWork(service.otherWork) : [];
  const pageTotal = 1 + otherWorkPages.length;
  const pageLabel = (index: number) =>
    pageTotal === 1 ? 'SERVICE DELIVERY' : `SERVICE DELIVERY ${index}/${pageTotal}`;

  return (
    <>
      <PdfPage section="serviceDelivery" issueLabel={issueLabel} pageLabel={pageLabel(1)}>
        <div className="pdf-section-heading">
          <p>SERVICE DELIVERY</p>
          <h2>Work completed across your security environment.</h2>
          <div className="pdf-rule" />
        </div>
        <div className="pdf-service-grid">
          {groups.map(([label, items]) => (
            <KeepTogether className="pdf-service-card" key={label}>
              <h3>{label}</h3>
              {items.length ? <ul>{items.map((item) => <li key={item}><InlineMd>{item}</InlineMd></li>)}</ul> : <p>No changes recorded.</p>}
            </KeepTogether>
          ))}
          <KeepTogether className="pdf-service-card pdf-service-card--wide">
            <h3>Proactive hunting</h3>
            <p>
              {service.hunts.summary
                ? <InlineMd>{service.hunts.summary}</InlineMd>
                : `${service.hunts.count ?? 0} hunting activities completed.`}
            </p>
            {!!service.hunts.resultingAnalytics.length && <ul>{service.hunts.resultingAnalytics.map((item) => <li key={item}><InlineMd>{item}</InlineMd></li>)}</ul>}
          </KeepTogether>
        </div>
      </PdfPage>
      {otherWorkPages.map((markdown, index) => (
        <PdfPage
          section="serviceDelivery"
          issueLabel={issueLabel}
          pageLabel={pageLabel(index + 2)}
          key={`service-other-${index}`}
        >
          <div className="pdf-section-heading pdf-section-heading--compact">
            <p>SERVICE DELIVERY</p>
            <h2>{index === 0 ? 'Other work this month.' : `Other work continued - ${index + 1}/${otherWorkPages.length}.`}</h2>
            <div className="pdf-rule" />
          </div>
          <div className="pdf-service-grid">
            <KeepTogether className="pdf-service-card pdf-service-card--wide">
              <Md className="pdf-markdown">{markdown}</Md>
            </KeepTogether>
          </div>
        </PdfPage>
      ))}
    </>
  );
}

function ClosingPage({ report, sections, issueLabel }: { report: ReportDetail; sections: AuthoredSections; issueLabel: string }) {
  return (
    <PdfPage section="closing" issueLabel={issueLabel} pageLabel="ABOUT THIS REPORT">
      <div className="pdf-closing">
        <div>
          <p className="pdf-kicker">ABOUT THIS REPORT</p>
          <h2>Security operations, translated into decisions.</h2>
          <p><InlineMd>{sections.colophon.bodyText}</InlineMd></p>
        </div>
        <div className="pdf-closing__signoff">
          <img src="/alexein-logo.svg" alt="Alexein" />
          <span>Prepared for</span>
          <strong>{report.tenantDisplayName}</strong>
          <span>{sections.colophon.signedBy}</span>
        </div>
      </div>
    </PdfPage>
  );
}

function CustomOverridePage({
  section,
  title,
  content,
  issueLabel,
}: {
  section: PdfSectionKey;
  title: string;
  content: string;
  issueLabel: string;
}) {
  return (
    <PdfPage section={section} issueLabel={issueLabel} pageLabel={title.toUpperCase()}>
      <div className="pdf-section-heading">
        <p>ANALYST-AUTHORED SECTION</p>
        <h2>{title}</h2>
        <div className="pdf-rule" />
      </div>
      <Md className="pdf-custom-content">{content}</Md>
    </PdfPage>
  );
}

export function PdfReportDocument({
  report,
  template = DEFAULT_PDF_TEMPLATE,
  telemetry = null,
}: {
  report: ReportDetail;
  template?: PdfTemplateConfig;
  /** Snapshot frozen into generationParamsJson at generation time. Null for
   *  reports generated before telemetry existed — the chart sections say so. */
  telemetry?: ReportTelemetry | null;
}) {
  const sections = report.aiGeneratedContent;
  if (!sections) return <div className="pdf-document-error">Report authored content is unavailable.</div>;

  const issueLabel = `ISSUE ${sections.issueNumber} - ${format(new Date(report.periodStart), 'MMMM yyyy').toUpperCase()}`;
  const incidentPages = chunkForPdf(sections.retroCards ?? [], 2);
  const industryPages = chunkForPdf(sections.industryCards ?? [], 2);
  const overrides = new Map(
    report.customContent
      .filter((item) => item.position === 'replace')
      .map((item) => [item.sectionName, item.customText]),
  );
  const style = {
    '--pdf-navy': template.theme.navy,
    '--pdf-blue': template.theme.blue,
    '--pdf-lime': template.theme.lime,
    '--pdf-paper': template.theme.paper,
    '--pdf-ink': template.theme.ink,
    '--pdf-muted': template.theme.muted,
  } as CSSProperties;

  /**
   * Enabled-but-empty chart sections are dropped rather than rendered as a page
   * of "not available" notes. Only affects the telemetry sections: the authored
   * sections always have content, because the agent writes them.
   */
  const suppressed = new Set<PdfSectionKey>();
  if (!hasPostureContent(telemetry)) suppressed.add('posture');
  if (!hasEmailSecurityContent(telemetry)) suppressed.add('emailSecurity');

  const content: Record<PdfSectionKey, ReactNode> = {
    cover: overrides.has('cover')
      ? <CustomOverridePage section="cover" title="Cover statement" content={overrides.get('cover')!} issueLabel={issueLabel} />
      : (
        <CoverPage
          report={report}
          sections={sections}
          showBriefing={!template.enabledSections.incidents}
          coverVariant={template.coverVariant}
        />
      ),
    executive: overrides.has('spread')
      ? <CustomOverridePage section="executive" title="Executive summary" content={overrides.get('spread')!} issueLabel={issueLabel} />
      : <ExecutivePage sections={sections} issueLabel={issueLabel} />,
    metrics: overrides.has('featureIntro') || overrides.has('mitre')
      ? <CustomOverridePage section="metrics" title="The month in data" content={overrides.get('featureIntro') ?? overrides.get('mitre')!} issueLabel={issueLabel} />
      : <MetricsPage report={report} sections={sections} issueLabel={issueLabel} />,
    posture: (
      <ChartPage title="Security posture" issueLabel={issueLabel}>
        <PdfPostureSection telemetry={telemetry} />
      </ChartPage>
    ),
    emailSecurity: (
      <ChartPage title="Email security" issueLabel={issueLabel}>
        <PdfEmailSecuritySection telemetry={telemetry} />
      </ChartPage>
    ),
    execDigest: (
      <ChartPage title="The month at a glance" issueLabel={issueLabel} subtitle={null}>
        <PdfExecDigestSection
          telemetry={telemetry}
        />
      </ChartPage>
    ),
    incidents: overrides.has('retro')
      ? <CustomOverridePage section="incidents" title="Incident retrospective" content={overrides.get('retro')!} issueLabel={issueLabel} />
      : incidentPages.map((cards, index) => (
      <IncidentPage cards={cards} issueLabel={issueLabel} pageIndex={index + 1} pageTotal={incidentPages.length} key={`incident-${index}`} />
      )),
    industry: overrides.has('industry')
      ? <CustomOverridePage section="industry" title="Industry intelligence" content={overrides.get('industry')!} issueLabel={issueLabel} />
      : industryPages.map((cards, index) => (
      <IndustryPage cards={cards} issueLabel={issueLabel} pageIndex={index + 1} pageTotal={industryPages.length} key={`industry-${index}`} />
      )),
    serviceDelivery: sections.serviceDelivery ? <ServiceDeliveryPages sections={sections} issueLabel={issueLabel} /> : null,
    closing: overrides.has('colophon')
      ? <CustomOverridePage section="closing" title="About this report" content={overrides.get('colophon')!} issueLabel={issueLabel} />
      : <ClosingPage report={report} sections={sections} issueLabel={issueLabel} />,
  };

  return (
    <div
      className={`pdf-document pdf-document--${template.coverVariant}`}
      style={style}
      data-pdf-template-id={template.id}
      data-pdf-template-version={template.version}
    >
      {template.sectionOrder.map((section) =>
        template.enabledSections[section] && !suppressed.has(section) ? content[section] : null,
      )}
    </div>
  );
}
